import { CSSProperties } from 'react';
import { ReactSVG } from 'react-svg';
import {
  ControllerFieldState,
  ControllerRenderProps,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import {
  FormErrorMessage,
  FormControl,
  Radio,
  // Heading,
  Flex,
  // FormHelperText,
  Text,
  useColorModeValue,
  Box,
  RadioProps,
  BoxProps,
  VisuallyHidden,
} from '@chakra-ui/react';
// import { Trans } from 'react-i18next';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { IconDefinition } from '@fortawesome/pro-duotone-svg-icons';

import { colorSemanticTokens } from 'chakra/colors';
import FontAwesomeIcon from 'components/primatives/FontAwesomeIcon';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Field = ControllerRenderProps<any, string>;

type Option = {
  value: string;
  label: BoxProps['children'];
  thumbnail?: BoxProps['children'];
  icon?: string | IconDefinition;
  props?: RadioProps;
  isDisabled?: boolean;
};

export type RhfStyledRadioFieldProps = {
  field: Field;
  fieldState: Pick<ControllerFieldState, 'isDirty' | 'error'>;
  label: string;
  isRequired?: boolean;
  isComplex?: boolean;
  isColumns?: boolean;
  options: Option[];
  additionalOptions?: Option[];
  suffix?: JSX.Element;
  hideLabel?: boolean;
};

export const Option = ({
  option,
  field,
  isChecked,
  isDisabled,
  isColumns,
  isComplex,
  ...props
}: {
  option: Option;
} & Pick<RadioProps, 'isChecked' | 'isDisabled'> &
  Pick<RhfStyledRadioFieldProps, 'isComplex' | 'isColumns' | 'field'>) => {
  const faPrimaryColor = useColorModeValue(
    colorSemanticTokens.tmtBlue,
    colorSemanticTokens.tmtGold,
  );
  const faCheckedSecondaryColor = useColorModeValue('white', 'black');
  const faUncheckedSecondaryColor = useColorModeValue(
    colorSemanticTokens.tmtBlue,
    colorSemanticTokens.tmtGold,
  );

  return (
    <Radio
      variant={isComplex ? 'column' : 'button'}
      w={isComplex && isColumns ? { base: '100%', sm: '50%' } : undefined}
      minW={!isComplex ? 150 : undefined}
      justifyContent="center"
      isChecked={isChecked}
      isDisabled={isDisabled}
      {...field}
      value={option.value}
      {...props}
    >
      {option.thumbnail}
      {option.icon && typeof option.icon !== 'string' && (
        <FontAwesomeIcon
          icon={option.icon}
          style={
            isChecked
              ? ({
                  '--fa-primary-color': faCheckedSecondaryColor,
                  '--fa-primary-opacity': 1,
                  '--fa-secondary-color': faCheckedSecondaryColor,
                  '--fa-secondary-opacity': 0.5,
                } as CSSProperties)
              : ({
                  '--fa-primary-color': faPrimaryColor,
                  '--fa-primary-opacity': 1,
                  '--fa-secondary-color': faUncheckedSecondaryColor,
                  '--fa-secondary-opacity': 0.5,
                } as CSSProperties)
          }
        />
      )}
      {option.icon && typeof option.icon === 'string' && (
        <div className={`svg-container radio-svg`}>
          <ReactSVG src={option.icon} />
        </div>
      )}
      <Box as="span" flex={1}>
        {option.label}
      </Box>
      <Box className="checkmark" display="none">
        <FontAwesomeIcon icon={faCheckCircle} />
      </Box>
    </Radio>
  );
};

export const RhfStyledRadioField = ({
  label,
  options,
  additionalOptions,
  isRequired = false,
  isComplex = false,
  isColumns = true,
  field,
  fieldState,
  suffix,
  hideLabel = false,
}: RhfStyledRadioFieldProps) => {
  return (
    <FormControl
      id={field.name}
      isRequired={isRequired}
      as="fieldset"
      aria-describedby={isRequired ? `required-${field.name}` : undefined}
      mt={0}
    >
      {hideLabel ? (
        <VisuallyHidden>
          <Box as="legend" id={`legend-${field.name}`}>
            {label}
            {isRequired && (
              <Text as="span" layerStyle="requiredIndicator">
                *
              </Text>
            )}
          </Box>
        </VisuallyHidden>
      ) : (
        <Box
          as="legend"
          id={`legend-${field.name}`}
          w="100%"
          fontFamily="heading"
          textTransform="uppercase"
          fontWeight="bold"
          fontSize="xl"
          textAlign="center"
          mb={2}
        >
          {label}
          {isRequired && (
            <Text as="span" layerStyle="requiredIndicator">
              *
            </Text>
          )}
        </Box>
      )}

      <Flex
        aria-labelledby={`legend-${field.name}`}
        wrap="wrap"
        role="group"
        textAlign="left"
        my={0}
        {...(!isComplex
          ? {
              direction: 'row',
              justify: 'center',
              gap: 2,
            }
          : {
              direction: isColumns ? { base: 'column', sm: 'row' } : 'column',
              gap: isColumns ? { base: 1, sm: 0 } : 1,
            })}
      >
        {options.map(({ props, ...option }, index) => (
          <Option
            key={index}
            option={option}
            field={field}
            isChecked={
              Array.isArray(field.value)
                ? field.value.includes(option.value)
                : field.value === option.value
            }
            isDisabled={option.isDisabled}
            isColumns={isColumns}
            isComplex={isComplex}
            {...(isComplex && {
              w: isColumns ? { base: '100%', sm: '50%' } : '100%',
            })}
            {...props}
          />
        ))}
        {suffix}
        {(additionalOptions || []).map((option, index) => (
          <Option
            key={index}
            option={option}
            field={field}
            isChecked={
              Array.isArray(field.value)
                ? field.value.includes(option.value)
                : field.value === option.value
            }
            isColumns={isColumns}
            isComplex={isComplex}
          />
        ))}
      </Flex>
      <FormErrorMessage>
        {fieldState.error && <p>{fieldState.error.message}</p>}
      </FormErrorMessage>
      {/* {isRequired && (
        <FormHelperText layerStyle="requiredText" id={`required-${field.name}`}>
          <Text as="span" aria-hidden="true" color="red.400">
            *
          </Text>
          <Trans>This is a required field.</Trans>
        </FormHelperText>
      )} */}
    </FormControl>
  );
};

const RhfStyledRadioFieldContainer = ({
  label,
  hideLabel,
  options,
  isRequired,
  isComplex,
  isColumns,
  additionalOptions,
  suffix,
  ...props // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: UseControllerProps<any> &
  Pick<
    RhfStyledRadioFieldProps,
    | 'label'
    | 'hideLabel'
    | 'options'
    | 'isRequired'
    | 'isComplex'
    | 'isColumns'
    | 'additionalOptions'
    | 'suffix'
  >) => {
  const controler = useController(props);
  return (
    <RhfStyledRadioField
      label={label}
      hideLabel={hideLabel}
      options={options}
      additionalOptions={additionalOptions}
      suffix={suffix}
      isRequired={isRequired}
      isComplex={isComplex}
      isColumns={isColumns}
      {...controler}
    />
  );
};

export default RhfStyledRadioFieldContainer;
